import { TuiActiveZone } from '@taiga-ui/cdk';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  model,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { TuiDropdown, TuiIcon, TuiButton, TuiCheckbox } from '@taiga-ui/core';
import { TuiTiles } from '@taiga-ui/kit';
import { TranslatePipe } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import {
  INITIAL_DESKTOP_COLUMNS,
  INITIAL_DESKTOP_ROWS_PER_PAGE,
  INITIAL_DESKTOP_ROWS_SETTINGS,
  X_TABLE_COLUMNS_ORDER,
  X_TABLE_ROWS_SETTINGS,
} from '@core/constants';
import { LocalStorageService } from '@core/services/local-storage.service';
import { AccountService } from '@core/services/account/account.service';
import {
  OutkeepSettings,
  RequestTableColumn,
  RowSettingItem,
  RowsSettings,
} from '@core/types';
import { TilesOrderDirective } from '@common/directives/tiles-order.directive';
import { BehaviorSubject } from 'rxjs';
import { IsLoadingService } from '@core/services/is-loading.service';

@Component({
  selector: 'x-edit-desktop-table-button',
  imports: [
    TuiButton,
    TuiDropdown,
    TranslatePipe,
    TuiTiles,
    TuiIcon,
    TuiCheckbox,
    FormsModule,
    TilesOrderDirective,
    TuiActiveZone,
  ],
  templateUrl: './edit-desktop-table-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditDesktopTableButtonComponent implements OnInit {
  $tableColumns = model<RequestTableColumn[]>([], {
    alias: 'tableColumns',
  });

  $tableSettingsChanged = output<OutkeepSettings>({
    alias: 'tableSettingsChanged',
  });

  private readonly localStorageService = inject(LocalStorageService);
  private readonly accountService = inject(AccountService);
  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);
  public initialColumns: RequestTableColumn[] = INITIAL_DESKTOP_COLUMNS;
  protected rowSettingsArray: RowSettingItem[] = INITIAL_DESKTOP_ROWS_SETTINGS;
  public $pendingTableSettings = signal<OutkeepSettings>({});

  colsOrder: Map<number, number> = new Map();
  columnsChecked: Record<string, boolean> = {};

  protected $open = signal<boolean>(false);
  private $columnsSettings = viewChild<ElementRef>('columnsSettings');

  constructor() {
    effect(() => {
      if (this.$tableColumns()) {
        for (const column of this.initialColumns) {
          this.columnsChecked[column] = this.$tableColumns().includes(column);
        }
      }
    });
  }

  ngOnInit(): void {
    const storedColumnsOrder = this.localStorageService.getItem<
      Map<number, number>
    >(X_TABLE_COLUMNS_ORDER);

    if (storedColumnsOrder) {
      this.colsOrder = new Map(storedColumnsOrder);
    }

    const storedRowsSettings = this.localStorageService.getItem<RowsSettings>(
      X_TABLE_ROWS_SETTINGS,
    );

    if (storedRowsSettings) {
      this.rowSettingsArray = this.rowSettingsArray.map(({ key }) => ({
        key,
        value: storedRowsSettings[key],
      }));
    }

    for (const column of this.initialColumns) {
      this.columnsChecked[column] = false;
    }
  }

  public ratingCheckedChange(
    column: RequestTableColumn,
    checked: boolean,
  ): void {
    const tableColumns = this.$tableColumns();
    const newTableColumns = checked
      ? this.getSortedColumns([...tableColumns, column])
      : tableColumns.filter(col => col !== column);

    const settings = {
      tableColumns: newTableColumns,
    };

    this.$pendingTableSettings.set({ ...settings });
    this.$tableSettingsChanged.emit({ ...settings });
  }

  private getRowsSettingsFromArray(arr: RowSettingItem[]): RowsSettings {
    return arr.reduce(
      (acc: RowsSettings, { key, value }: RowSettingItem) => ({
        ...acc,
        [key]: value,
      }),
      {},
    );
  }

  protected rowSettingsChange() {
    const rowsSettings = this.getRowsSettingsFromArray(this.rowSettingsArray);

    this.$pendingTableSettings.set({ rowsSettings });
    this.$tableSettingsChanged.emit({ rowsSettings });
  }

  public onColumnsOrderChange(order: Map<number, number>): void {
    const newOrder = Array.from(order.entries());

    const updatedColumns = this.createColumnFromOrderMap(order);

    const settings = {
      tableColumns: updatedColumns,
      tableColumnsOrder: newOrder,
    };

    this.$pendingTableSettings.set(settings);
    this.$tableSettingsChanged.emit(settings);
  }

  public resetTableSettings(): void {
    this.colsOrder = new Map();
    this.rowSettingsArray = [...INITIAL_DESKTOP_ROWS_SETTINGS];

    this.accountService.deleteUserSettingsFromLocalStorage();

    const newSettings: OutkeepSettings = {
      tableColumns: this.initialColumns,
      tableColumnsOrder: null,
      rowsSettings: this.getRowsSettingsFromArray(
        INITIAL_DESKTOP_ROWS_SETTINGS,
      ),
      tableColumnsWidths: null,
      rowsPerPage: INITIAL_DESKTOP_ROWS_PER_PAGE,
    };

    this.$pendingTableSettings.set(newSettings);
    this.$tableSettingsChanged.emit(newSettings);
  }

  private createColumnFromOrderMap(
    order: Map<number, number>,
  ): RequestTableColumn[] {
    if (!order.size) {
      // If there is no order, return the initial columns
      return this.initialColumns;
    }

    const updatedColumns: RequestTableColumn[] = [];

    order.forEach((currentIndex, initialIndex) => {
      if (this.$tableColumns().includes(this.initialColumns[initialIndex])) {
        updatedColumns[currentIndex] = this.initialColumns[initialIndex];
      }
    });

    return updatedColumns.filter(col => col !== undefined);
  }

  private getSortedColumns(columns: RequestTableColumn[]) {
    return columns.toSorted((a, b) => {
      // Если колонки когда-либо меняли свой порядок, то он задан в this.colsOrder
      // Иначе порядок задан в this.initialColumns
      const initialIndexA = this.initialColumns.indexOf(a);
      const currentIndexA = this.colsOrder.get(initialIndexA) ?? initialIndexA;
      const initialIndexB = this.initialColumns.indexOf(b);
      const currentIndexB = this.colsOrder.get(initialIndexB) ?? initialIndexB;

      return currentIndexA - currentIndexB;
    });
  }

  toggle() {
    this.$open.set(!this.$open());
  }

  onActiveZone(active: boolean): void {
    if (active) return; // фокус пришел внутрь editTableDropdown

    // фокус ушел из editTableDropdown потому что начался лоадер
    if (this.isLoading$.value) {
      return this.$columnsSettings()?.nativeElement.focus();
    }

    // клик снаружи editTableDropdown
    this.$open.set(false);
  }
}
